import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faMapMarkerAlt, faSearch, faList, faThLarge, faChevronDown, faFilter } from "@fortawesome/free-solid-svg-icons";

import { URLS } from "../../services/urls";
import { API } from "../../services/axios";
import { DEFAULT_PAGE_COUNT } from "../../components/utils/consts";

import NewsLetter from "../../components/newsletter";
import JobCard from "../../components/jobs/JobCard";
import JobCardList from "./components/JobCardList";
import SearchFilter from "./components/SearchFilter"; // Assurez-vous que ce composant existe et est propre
import ApplyButton from "../../components/actions/ApplyButton";

import "../jobDetails/JobDetStyle.css"; // Ou votre fichier CSS global

const fetcher = (url) => API.get(url).then(res => {
  // Filtrage des villes principales du Cameroun
  const mainCities = ["Douala", "Yaoundé", "Bafoussam", "Garoua", "Maroua", "Bamenda", "Buea", "Bertoua"];
  return res.data.results.filter(element => mainCities.includes(element.name));
});

const JobSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();

  // --- STATES ---
  const [view, setView] = useState("LIST");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(null); // Objet complet
  const [location, setLocation] = useState(null); // Objet complet
  
  // États pour les dropdowns custom
  const [showIndustries, setShowIndustries] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  
  // Refs pour click outside
  const industryRef = useRef(null);
  const locationRef = useRef(null);

  // --- DATA FETCHING ---
  const { data: locations } = useSWR(URLS.CONFIGS.PROFILE.COUNTRIES.CITIES.cmr(""), fetcher);
  const { data: categories } = useSWR(URLS.CONFIGS.PROFILE.SECTOR.list(""));
  
  // Construction de la requête de recherche
  const { data: jobs, isLoading: loadingJobs } = useSWR(
    URLS.JOBS.search(page, search, category?.id, location?.name)
  );

  // --- EFFECTS ---
  
  // Synchronisation URL -> State au chargement
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlIndId = searchParams.get("industry");
    const urlLocName = searchParams.get("location");

    setSearch(urlSearch);

    // Si on a les catégories chargées, on retrouve l'objet correspondant à l'ID
    if (urlIndId && categories?.results) {
        const foundCat = categories.results.find(c => c.id.toString() === urlIndId);
        if (foundCat) setCategory(foundCat);
    }

    // Idem pour la location
    if (urlLocName && locations) {
        const foundLoc = locations.find(l => l.name === urlLocName);
        if (foundLoc) setLocation(foundLoc);
    }

  }, [searchParams, categories, locations]);

  // Click Outside pour fermer les dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
        if (industryRef.current && !industryRef.current.contains(event.target)) {
            setShowIndustries(false);
        }
        if (locationRef.current && !locationRef.current.contains(event.target)) {
            setShowLocation(false);
        }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- HANDLERS ---

  const handleChangePage = (event, pg) => {
    event.preventDefault();
    setPage(pg);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeView = (event, newView) => {
    event.preventDefault();
    setView(newView);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setPage(1); // Reset page on search
    navigate(
      `/search?search=${search}&industry=${category?.id ?? ""}&location=${location?.name ?? ""}`
    );
  };

  return (
    <>
      {/* --- HERO SECTION AVEC BARRE DE RECHERCHE --- */}
      <section className="section-box-2 pt-50 pb-50 bg-light">
        <div className="container">
          <div className="text-center mb-40">
            <h2 className="mb-15 wow animate__animated animate__fadeInUp">
              <span className="color-brand-2">{jobs?.count || 0} {t("textJobs")}</span> {t("textAvailableNow")}
            </h2>
            <p className="font-md color-text-paragraph-2 wow animate__animated animate__fadeInUp">
              {t("textFindTheJOb")}
            </p>
          </div>

          <div className="wow animate__animated animate__fadeInUp" data-wow-delay=".2s">
            <form onSubmit={handleSubmit} className="modern-search-bar mx-auto">
                
                {/* Industrie Dropdown */}
                <div className="search-group" ref={industryRef}>
                    <div 
                        className="search-input-wrapper cursor-pointer"
                        onClick={() => setShowIndustries(!showIndustries)}
                    >
                        <FontAwesomeIcon icon={faBriefcase} className="search-icon text-muted" />
                        <div className="flex-grow-1 ms-2">
                            <span className="label-small text-muted d-block">{t("textIndustry")}</span>
                            <span className="value-text text-dark fw-bold text-truncate d-block">
                                {category?.name ?? "Tout secteur"}
                            </span>
                        </div>
                        <FontAwesomeIcon icon={faChevronDown} className={`chevron-icon ${showIndustries ? 'rotate' : ''}`} />
                    </div>
                    {showIndustries && (
                        <div className="custom-dropdown-menu shadow-lg">
                            <div className="dropdown-item" onClick={() => { setCategory(null); setShowIndustries(false); }}>Tout secteur</div>
                            {categories?.results?.map((cat) => (
                                <div 
                                    key={cat.id} 
                                    className={`dropdown-item ${category?.id === cat.id ? 'active' : ''}`}
                                    onClick={() => { setCategory(cat); setShowIndustries(false); }}
                                >
                                    {cat.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Localisation Dropdown */}
                <div className="search-group" ref={locationRef}>
                    <div 
                        className="search-input-wrapper cursor-pointer"
                        onClick={() => setShowLocation(!showLocation)}
                    >
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="search-icon text-muted" />
                        <div className="flex-grow-1 ms-2">
                            <span className="label-small text-muted d-block">{t("textLocation")}</span>
                            <span className="value-text text-dark fw-bold text-truncate d-block">
                                {location?.name ?? "Toute ville"}
                            </span>
                        </div>
                        <FontAwesomeIcon icon={faChevronDown} className={`chevron-icon ${showLocation ? 'rotate' : ''}`} />
                    </div>
                    {showLocation && (
                        <div className="custom-dropdown-menu shadow-lg">
                            <div className="dropdown-item" onClick={() => { setLocation(null); setShowLocation(false); }}>Toute ville</div>
                            {locations?.map((loc) => (
                                <div 
                                    key={loc.id} 
                                    className={`dropdown-item ${location?.id === loc.id ? 'active' : ''}`}
                                    onClick={() => { setLocation(loc); setShowLocation(false); }}
                                >
                                    {loc.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Input Recherche */}
                <div className="search-group flex-grow-1 border-end-0">
                    <div className="search-input-wrapper">
                        <FontAwesomeIcon icon={faSearch} className="search-icon text-muted" />
                        <input
                            className="form-control border-0 shadow-none bg-transparent fw-bold"
                            type="text"
                            placeholder={t("textkeyword") || "Mots-clés..."}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {/* Bouton Submit */}
                <div className="search-action p-2">
                    <button type="submit" className="btn btn-gradient text-white fw-bold px-4 py-3 h-100 w-100">
                        {t("textSearch")}
                    </button>
                </div>
            </form>
          </div>
        </div>
      </section>

      {/* --- CONTENU PRINCIPAL --- */}
      <section className="section-box mt-50 mb-50">
        <div className="container">
          <div className="row">
            
            {/* COLONNE GAUCHE : FILTRES (Sidebar Standard) */}
            <div className="col-lg-3 col-md-12 col-sm-12 col-12 mb-5">
               <div className="sidebar-shadow none-shadow mb-30">
                  <div className="sidebar-filters">
                    <div className="filter-block head-border mb-30">
                      <h5>
                        <FontAwesomeIcon icon={faFilter} className="me-2 text-primary" /> 
                        Filtres avancés
                      </h5>
                    </div>
                    {/* Le composant SearchFilter doit gérer ses propres filtres internes */}
                    <SearchFilter 
                        locations={locations ?? []} 
                        searchResults={jobs?.results ?? []} 
                        count={jobs?.count ?? 0} 
                    />
                  </div>
               </div>
            </div>

            {/* COLONNE DROITE : RÉSULTATS */}
            <div className="col-lg-9 col-md-12 col-sm-12 col-12">
              <div className="content-page">
                
                {/* Barre d'outils (View Switcher & Sort) */}
                <div className="box-filters-job border-bottom pb-2 mb-30">
                  <div className="row align-items-center">
                    <div className="col-xl-6 col-lg-5">
                      <span className="text-small text-muted">
                         Affichage de <strong>{jobs?.results?.length || 0}</strong> offres sur <strong>{jobs?.count || 0}</strong>
                      </span>
                    </div>
                    <div className="col-xl-6 col-lg-7 text-lg-end mt-sm-15">
                      <div className="d-flex justify-content-lg-end align-items-center gap-2">
                        {/* View Switcher */}
                        <div className="btn-group" role="group">
                            <button 
                                type="button" 
                                className={`btn btn-sm ${view === "LIST" ? "btn-primary" : "btn-light border"}`}
                                onClick={(e) => handleChangeView(e, "LIST")}
                            >
                                <FontAwesomeIcon icon={faList} />
                            </button>
                            <button 
                                type="button" 
                                className={`btn btn-sm ${view === "GRID" ? "btn-primary" : "btn-light border"}`}
                                onClick={(e) => handleChangeView(e, "GRID")}
                            >
                                <FontAwesomeIcon icon={faThLarge} />
                            </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loading State */}
                {loadingJobs && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                )}

                {/* Empty State */}
                {!loadingJobs && jobs?.count === 0 && (
                  <div className="text-center py-5 bg-light rounded-3">
                     <h5 className="text-muted">{t("textNoJob")}</h5>
                     <p>Essayez d'élargir vos critères de recherche.</p>
                  </div>
                )}

                {/* Liste des Jobs */}
                <div className="row">
                    {view === "LIST" && jobs?.results?.map((job) => (
                        <div className="col-12 mb-3" key={"joblist" + job.id}>
                            <JobCardList job={job} />
                        </div>
                    ))}

                    {view === "GRID" && jobs?.results?.map((job) => (
                        <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 col-12 mb-4" key={"jobcard" + job.id}>
                            <JobCard job={job} />
                        </div>
                    ))}
                </div>

                {/* Pagination */}
                {jobs?.count > 0 && (
                  <div className="paginations mt-30">
                    <ul className="pager justify-content-center">
                      {/* Bouton Précédent */}
                      {page > 1 && (
                          <li>
                            <a href="#" className="pager-prev" onClick={(e) => handleChangePage(e, page - 1)}></a>
                          </li>
                      )}
                      
                      {/* Numéros de page (Simplifié pour l'exemple) */}
                      {Array.from({ length: Math.ceil(jobs.count / DEFAULT_PAGE_COUNT) }, (_, i) => i + 1)
                        .slice(Math.max(0, page - 2), Math.min(page + 1, Math.ceil(jobs.count / DEFAULT_PAGE_COUNT))) // Affiche 3 pages autour de la courante
                        .map((pg) => (
                            <li key={pg} className={page === pg ? "active" : ""}>
                                <a href="#" className="pager-number" onClick={(e) => handleChangePage(e, pg)}>
                                    {pg}
                                </a>
                            </li>
                      ))}

                      {/* Bouton Suivant */}
                      {page < Math.ceil(jobs.count / DEFAULT_PAGE_COUNT) && (
                          <li>
                            <a href="#" className="pager-next" onClick={(e) => handleChangePage(e, page + 1)}></a>
                          </li>
                      )}
                    </ul>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsLetter />
      
      {/* Modals ApplyButton cachés (pour le fonctionnement des popups) */}
      {jobs?.results?.map((job) => (
        <ApplyButton
          key={job.id + "apply-modal"}
          hideModal={false}
          hideButton={true} // Le bouton est déjà dans la carte, ici on charge juste la modale invisible
          jobId={job.id}
          job={job}
        />
      ))}
    </>
  );
};

export default JobSearch;